import type { JobsModeratorsCreateVacancyInput, JobsModeratorsEmployer, JobsModeratorsVacancy } from '@/types/graphql';
import { JobsRole } from '@/types/graphql';
import Vue from 'vue';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import {
  fieldWasNotTouched,
  FormKeyForFieldType,
  getFieldDefaultValue,
  getFieldValidationRules,
  getValueIdentifierForFieldType,
  getValueParserForFieldType,
  JobsModeratorsFieldsFormGroupKey,
  JobsModeratorsFieldsType,
  JobsStaticFieldsType,
  vacancyFieldValueKey,
} from '~/components/partials/jobs/JobsBackOffice/JobsBackOfficeVacancy/JobsBackOfficeVacancyForm/vacancyFormHelpers';
import type { RootState } from '~/types/store';
import type {
  Category,
  CreateVacancyForm,
  Field,
  FormFieldMetaData,
  JobsBackOfficeCreateVacancy,
} from '~/types/store/create-vacancy';

export const state = (): JobsBackOfficeCreateVacancy => ({
  form: {
    role: JobsRole.AccountExecutive,
  } as CreateVacancyForm,
  categories: [],
  formFieldIds: {},
  selectedDynamicFields: [],
  formMetaData: {},
  employer: null,
});

export const mutations: MutationTree<JobsBackOfficeCreateVacancy> = {
  setForm(state, formData: CreateVacancyForm) {
    state.form = {
      ...formData,
    };
  },
  setFormFieldIds(state, fieldIds: Record<string, string>) {
    state.formFieldIds = {
      ...fieldIds,
    };
  },
  updateField(state, { slug, value }) {
    Vue.set(state.form, slug, value);
  },
  updateCategories(state, categories: Category[]) {
    Vue.set(state, 'categories', categories);
  },
  updateSelectedDynamicFields(state, value: Array<Field>) {
    state.selectedDynamicFields = [...value];
  },
  clearSelectedDynamicFields(state) {
    state.selectedDynamicFields = [];
  },
  setFormFieldMetaData(state, value: Record<string, FormFieldMetaData>) {
    state.formMetaData = { ...value };
  },
  updateFormFieldMetaData(state, { value, slug }: { value: FormFieldMetaData; slug: string }) {
    Vue.set(state.formMetaData, slug, value);
  },
  clearFormFieldMetaData(state, slug: string) {
    Vue.set(state.formMetaData, slug, undefined);
  },
  setEmployer(state, employer: JobsModeratorsEmployer | null) {
    state.employer = employer;
  },
};

export const getters: GetterTree<JobsBackOfficeCreateVacancy, RootState> = {
  allPossibleFields(state): Array<Field> {
    const allRoles = [JobsRole.AccountExecutive, JobsRole.CsmAm, JobsRole.Sdr, JobsRole.Vp, JobsRole.Other];
    return state.categories.reduce(
      (acc: Array<Field>, category: Category) => {
        const fields = category.fields.map(field => ({ ...field, isDynamicField: true }));
        acc.push(...fields);
        return acc;
      },
      [
        {
          slug: 'employer',
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect,
          requiredForRoles: allRoles,
        },
        {
          slug: 'headcount',
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsInteger,
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsString,
          slug: 'title',
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsString,
          slug: 'description',
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsFloat,
          slug: 'matchScoreThreshold',
          requiredForRoles: [],
        },
        {
          type: JobsStaticFieldsType.JobsStaticFieldsToggle,
          slug: 'autoCandidateSubmissionsEnabled',
          requiredForRoles: [],
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelect,
          slug: 'countries',
          multiselect: true,
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord,
          slug: 'cities',
          requiredForRoles: state.form.countries?.length ? [] : allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean,
          slug: 'openToRemoteWork',
          requiredForRoles: [],
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsString,
          slug: 'mergeWithVacancyId',
          requiredForRoles: [],
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsBoolean,
          slug: 'remoteOnlyAtSelectedLocations',
          requiredForRoles: [],
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect,
          slug: 'employmentType',
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsMultiSelectRecord,
          slug: 'recruiterIds',
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsSingleSelect,
          slug: 'primaryRecruiterId',
          requiredForRoles: allRoles,
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsString,
          slug: 'comments',
          requiredForRoles: [],
        },
        {
          type: JobsModeratorsFieldsType.JobsModeratorsFieldsString,
          slug: 'contractId',
          requiredForRoles: allRoles,
        },
      ] as Array<Field>,
    );
  },
  formFields(state, getters): Array<Field> {
    return getters.allPossibleFields.filter((field: Field) => typeof state.form[field.slug] !== 'undefined');
  },
  rules(state, getters): CreateVacancyForm {
    return getters.formFields.reduce((acc: Record<string, unknown>, field: Field) => {
      const validation = getFieldValidationRules(field, state.form, state.selectedDynamicFields, state.employer);
      if (Object.keys(validation).length) acc[field.slug] = validation;
      return acc;
    }, {});
  },
};

export const actions: ActionTree<JobsBackOfficeCreateVacancy, RootState> = {
  setVacancyDataOnForm({ commit }, { vacancy }: { fields: Array<Field>; vacancy: JobsModeratorsVacancy | null }) {
    const vacancyValues: Record<string, unknown> = {
      title: vacancy?.title,
      cities: vacancy?.cities,
      comments: vacancy?.comments,
      headcount: vacancy?.headcount,
      matchScoreThreshold: vacancy?.matchScoreThreshold,
      countries: vacancy?.countries?.map(country => ({ name: country.name, label: country.name })),
      description: vacancy?.description,
      employer: vacancy?.employer ? { id: vacancy.employer.id, name: vacancy.employer.company.name } : null,
      employmentType: vacancy?.employmentType,
      role: vacancy?.position,
      recruiterIds: vacancy?.recruiters?.map(recruiter => ({
        value: recruiter.id,
        label: `${recruiter.firstname} ${recruiter.lastname}`,
      })),
      primaryRecruiterId: vacancy?.recruiters?.find(recruiter => recruiter.primary)?.id ?? '',
      openToRemoteWork: vacancy?.openToRemoteWork,
      remoteOnlyAtSelectedLocations: vacancy?.remoteOnlyAtSelectedLocations,
      autoCandidateSubmissionsEnabled: vacancy?.autoCandidateSubmissionsEnabled,
      contractId: vacancy?.contract?.id,
    };

    const vacancyValuesIds = {} as Record<string, string>;
    const vacancyValuesMetaData = {} as Record<string, FormFieldMetaData>;

    vacancy?.fieldValues?.forEach(value => {
      // @ts-ignore
      const key = vacancyFieldValueKey[value.field.__typename];
      vacancyValues[value.field.slug] = (value as Record<string, unknown>)[key];
      vacancyValuesIds[value.field.slug] = value.id;
      vacancyValuesMetaData[value.field.slug] = {
        criteria: value.priority ?? 1,
        negation: value.negation ?? false,
        shouldClause: value.shouldClause ?? false,
      };
    });

    commit('setForm', vacancyValues);
    commit('setFormFieldIds', vacancyValuesIds);
    commit('setFormFieldMetaData', vacancyValuesMetaData);
  },
  setFormDefaultValues({ commit, state, getters }) {
    const defaultValues = getters.allPossibleFields.reduce(
      (acc: Record<string, unknown>, field: Field) => {
        const currentValue = state.form[field.slug];
        acc[field.slug] = fieldWasNotTouched(currentValue) ? getFieldDefaultValue(field.type) : currentValue;
        return acc;
      },
      { role: state.form.role || JobsRole.AccountExecutive },
    );

    commit('setForm', defaultValues);
  },
  async onCategoriesChange({ dispatch, commit }, { categories }: { categories: Array<Category> }) {
    commit('updateCategories', categories);
    await dispatch('setFormDefaultValues');
  },
  generateSubmitData({ state }, categories: Array<Category>): JobsModeratorsCreateVacancyInput {
    const fields = categories
      .filter(category => !category.isStaticCategory)
      .reduce((acc: Array<Field>, category: Category) => {
        return [...acc, ...category.fields];
      }, [] as Array<Field>);

    const dynamicFieldsProperties = Object.keys(state.form).reduce(
      (acc, key) => {
        const field = fields.find(field => field.slug === key);
        const fieldValue = state.form[key];
        if (!field) {
          return acc;
        }
        const resultKey = FormKeyForFieldType[field.type];
        const valueIdentifier = getValueIdentifierForFieldType[field.type];
        const valueParser = getValueParserForFieldType[field.type];
        const fieldMetadata = state.formMetaData[field.slug];
        const resultValue = {
          id: state.formFieldIds[key],
          fieldId: field.id,
          [valueIdentifier]: valueParser(fieldValue),
          negation: fieldMetadata.negation,
          shouldClause: fieldMetadata.shouldClause,
          priority: fieldMetadata.criteria,
        };

        acc[resultKey].push(resultValue);
        return acc;
      },
      {
        [JobsModeratorsFieldsFormGroupKey.booleanFields]: [],
        [JobsModeratorsFieldsFormGroupKey.floatFields]: [],
        [JobsModeratorsFieldsFormGroupKey.floatRangeFields]: [],
        [JobsModeratorsFieldsFormGroupKey.integerFields]: [],
        [JobsModeratorsFieldsFormGroupKey.integerRangeFields]: [],
        [JobsModeratorsFieldsFormGroupKey.multiSelectFields]: [],
        [JobsModeratorsFieldsFormGroupKey.multiSelectRecordFields]: [],
        [JobsModeratorsFieldsFormGroupKey.singleSelectFields]: [],
        [JobsModeratorsFieldsFormGroupKey.singleSelectRecordFields]: [],
        [JobsModeratorsFieldsFormGroupKey.stringArrayFields]: [],
        [JobsModeratorsFieldsFormGroupKey.stringFields]: [],
      } as Record<JobsModeratorsFieldsFormGroupKey, Array<unknown>>,
    );

    const submitData = {
      title: state.form.title,
      employerId: state.form.employer?.id,
      headcount: state.form.headcount,
      matchScoreThreshold: Number(state.form.matchScoreThreshold),
      description: state.form.description,
      cityIds: state.form.cities.map(city => city.id),
      countries: state.form.countries.map(country => country.label),
      openToRemoteWork: state.form.openToRemoteWork,
      remoteOnlyAtSelectedLocations: state.form.remoteOnlyAtSelectedLocations,
      position: state.form.role,
      recruiterIds: state.form.recruiterIds.map(recruiter => recruiter.value),
      primaryRecruiterId: state.form.primaryRecruiterId,
      employmentType: state.form.employmentType,
      comments: state.form.comments,
      autoCandidateSubmissionsEnabled: state.form.autoCandidateSubmissionsEnabled,
      contractId: state.form.contractId,
      mergeWithVacancyId: state.form.mergeWithVacancyId,
      ...dynamicFieldsProperties,
    };

    return submitData as unknown as JobsModeratorsCreateVacancyInput;
  },
  resetForm({ commit, state }): void {
    commit('setForm', { role: state.form.role });
    commit('setFormFieldIds', {});
    commit('setEmployer', null);
  },
  onUpdateSelectedDynamicFields({ commit, state, getters }, dynamicFields: Array<Field>) {
    const updatedFormFields: Array<Field> = getters.allPossibleFields.filter(
      (field: Field) =>
        !field.isDynamicField ||
        field.requiredForRoles.length ||
        dynamicFields.some(dynamicField => dynamicField.id === field.id),
    );
    const updatedFormData = updatedFormFields.reduce(
      (acc: Record<string, unknown>, field: Field) => {
        acc[field.slug] = state.form[field.slug] || getFieldDefaultValue(field.type);
        return acc;
      },
      {
        role: state.form.role,
      },
    );

    commit('setForm', updatedFormData);
  },
};
